import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import type {
  ChecklistEntry,
  ChecklistResponse
} from "@/api/types/foreignNationalDocument";

import { useOnClientInvalidate } from "../../clientProfile/lib/clientInvalidation";

type Result = {
  data: ChecklistResponse | null;
  isLoading: boolean;
  errorMessage: string | null;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  /**
   * The client document for an entry, saving a case-only file to the client
   * first (sharing, sending and signing all need one).
   */
  ensureDocumentId: (entry: ChecklistEntry) => Promise<string>;
  markReviewed: (entry: ChecklistEntry) => Promise<void>;
  setShared: (entry: ChecklistEntry, shared: boolean) => Promise<void>;
  remove: (entry: ChecklistEntry) => Promise<void>;
};

/** The client profile's documents checklist, kept fresh after uploads and agent edits. */
export function useDocumentsChecklist(
  foreignNationalId: string | null,
  opts: { enabled?: boolean } = {}
): Result {
  const enabled = opts.enabled ?? true;
  const [data, setData] = useState<ChecklistResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const seqRef = useRef(0);

  const refresh = useCallback(
    async (o?: { silent?: boolean }) => {
      if (!foreignNationalId || !enabled) return;
      seqRef.current += 1;
      const seq = seqRef.current;
      if (!o?.silent) {
        setIsLoading(true);
        setErrorMessage(null);
      }
      try {
        const next = await ForeignNationalDocumentsAPI.getChecklist(
          foreignNationalId
        );
        if (seq === seqRef.current) setData(next);
      } catch (err) {
        if (seq === seqRef.current)
          setErrorMessage(
            err instanceof Error ? err.message : "Couldn't load documents."
          );
      } finally {
        if (!o?.silent && seq === seqRef.current) setIsLoading(false);
      }
    },
    [enabled, foreignNationalId]
  );

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  useOnClientInvalidate(
    enabled ? foreignNationalId : null,
    ["documents"],
    () => {
      refresh({ silent: true }).catch(() => undefined);
    }
  );

  const ensureDocumentId = useCallback(
    async (entry: ChecklistEntry): Promise<string> => {
      if (entry.document_id) return entry.document_id;
      if (!foreignNationalId || !entry.node)
        throw new Error("This file can't be saved to the client.");
      const saved = await ForeignNationalDocumentsAPI.saveCaseDocument(
        foreignNationalId,
        entry.node.individual_id,
        entry.node.node_id
      );
      return saved.foreign_national_document_id;
    },
    [foreignNationalId]
  );

  const markReviewed = useCallback(
    async (entry: ChecklistEntry) => {
      if (!foreignNationalId || !entry.document_id) return;
      await ForeignNationalDocumentsAPI.updateFilingStatus(
        foreignNationalId,
        entry.document_id,
        "reviewed"
      );
      await refresh({ silent: true });
    },
    [foreignNationalId, refresh]
  );

  const setShared = useCallback(
    async (entry: ChecklistEntry, shared: boolean) => {
      if (!foreignNationalId) return;
      const id = await ensureDocumentId(entry);
      await ForeignNationalDocumentsAPI.setShared(
        foreignNationalId,
        id,
        shared
      );
      await refresh({ silent: true });
    },
    [ensureDocumentId, foreignNationalId, refresh]
  );

  const remove = useCallback(
    async (entry: ChecklistEntry) => {
      if (!foreignNationalId || !entry.document_id) return;
      await ForeignNationalDocumentsAPI.remove(
        foreignNationalId,
        entry.document_id
      );
      await refresh({ silent: true });
    },
    [foreignNationalId, refresh]
  );

  return useMemo(
    () => ({
      data,
      isLoading,
      errorMessage,
      refresh,
      ensureDocumentId,
      markReviewed,
      setShared,
      remove
    }),
    [
      data,
      ensureDocumentId,
      errorMessage,
      isLoading,
      markReviewed,
      refresh,
      remove,
      setShared
    ]
  );
}
